import { __decorate } from "tslib";
import { LitElement, html, css, customElement } from 'lit-element';
import '@cds/core/checkbox/register.js';
import '@cds/core/radio/register.js';
import '@cds/core/icon/register.js';
import { ClarityIcons, searchIcon, pencilIcon, angleIcon, ellipsisVerticalIcon, angleDoubleIcon, filterIcon, dragHandleIcon, viewColumnsIcon, timesIcon, pinIcon } from '@cds/core/icon';
import { internalProperty } from '@cds/core/internal';
import { getNextCellCoordinate } from './utils';
import './grid-row.element';
import './grid-cell.element';
import './grid-column.element';
import './grid-detail.element';
import './grid-footer.element';
import './grid-placeholder.element';
import './pagination.element';
import './dropdown.element';
import { CdsDraggableList } from './draggable-list';
import { baseStyles } from './styles';
ClarityIcons.addIcons(searchIcon, pencilIcon, angleIcon, ellipsisVerticalIcon, angleDoubleIcon, filterIcon, dragHandleIcon, viewColumnsIcon, timesIcon, pinIcon);
let CdsGrid = class CdsGrid extends LitElement {
    constructor() {
        super(...arguments);
        this.hasDraggableRows = false;
        this._rows = [];
        this.rowCount = 0;
    }
    static get styles() {
        return [baseStyles, css `
      [hidden] {
        display: none !important;
      }

      [rendering] tbody,
      [rendering] .footer {
        display: none;
        content-visibility: hidden;
      }

      :host {
        --body-height: auto;
        --grid-scroll-width: inital;
        --scrollbar-background: var(--cds-alias-object-container-background);
        --scrollbar-thumb-background: var(--cds-alias-object-border-color);
        --grid-template-columns: 1fr 1fr 1fr 1fr;
        contain: content;
      }

      .private-host {
        border: var(--cds-alias-object-border-width-100) solid var(--cds-alias-object-border-color);
        border-radius: var(--cds-alias-object-border-radius-100);
        background: var(--cds-alias-object-container-background);
        position: relative;
      }

      table {
        overflow: auto;
        position: relative;
        border-collapse: collapse;
        width: 100%;
        display: grid;
        align-items: start;
        align-content: start;
        grid-template-columns: var(--grid-template-columns);
        height: var(--body-height, 100%);
        scroll-padding:  var(--row-height, var(--cds-global-space-11));
      }

      .footer {
        background: var(--cds-alias-object-container-background);
      }

      thead, tbody, tr, td {
        display: contents !important;
      }

      ::slotted(cds-grid-row:first-of-type) {
        --internal-border-top: var(--cds-alias-object-border-width-100) solid transparent;
      }

      ::-webkit-scrollbar {
        width: 6px;
        height: 6px;
        background: var(--scrollbar-background);
      }

      ::-webkit-scrollbar-track {
        box-shadow: inset 0 0 11px var(--scrollbar-background);
      }

      ::-webkit-scrollbar-thumb {
        background: var(--scrollbar-thumb-background);
        border-radius: var(--cds-global-space-5);
        border: var(--cds-global-space-2) solid var(--scrollbar-background);
      }

      ::-webkit-scrollbar-corner {
        background: rgba(0,0,0,0);
      }

      @keyframes fadein {
        from {
          opacity: 0;
        }

        to {
          opacity: 1;
        }
      }
    `];
    }
    get columns() {
        return Array.from(this.querySelectorAll('cds-grid-column'));
    }
    get rows() {
        if (this._rows === null) {
            this._rows = Array.from(this.querySelectorAll('cds-grid-row'));
        }
        return this._rows;
    }
    get cells() {
        return this.rows.map(r => r.cells).flat();
    }
    get table() {
        return this.shadowRoot.querySelector('table');
    }
    render() {
        return html `
      <div class="private-host">
        <table role="grid" style="grid-template-rows: min-content ${this.rowCount > 0 ? `repeat(${this.rowCount}, minmax(min-content, max-content))` : ''} 1fr;">
          <thead>
            <tr @mousedown=${{ handleEvent: () => this.initializeColumns(), once: true }}>
              ${true ? html `<slot name="columns" @slotchange=${this.calculateColumns}></slot>` : ''}
            </tr>
          </thead>
          <tbody>
            ${true ? html `<slot></slot><slot name="placeholder"></slot>` : ''}
          </tbody>
        </table>
        <div class="footer">
          <slot name="footer"></slot>
        </div>
        <slot name="detail"></slot>
      </div>
    `;
    }
    firstUpdated(props) {
        super.firstUpdated(props);
        this.listenForRowUpdates();
        this.listenColumnSort();
        this.listenForCellKeyNaviation();
        this.listenForColumnHiddenChange();
        this.addEventListener('positionChange', (e) => {
            if (e.target.tagName.toLowerCase() === 'cds-grid-column') {
                this.calculateColumns();
            }
        });
    }
    listenForRowUpdates() {
        this.shadowRoot.addEventListener('slotchange', (e) => {
            if (e.target.name === '') {
                this._rows = null;
                this.rows.forEach((r, i) => r.row = i);
                this.rowCount = this.rows.length;
                this.columns.forEach((c, i) => c.col = i);
                this.setFirstCell();
            }
            if (!this.hasDraggableRows && this.rows.find(r => r.draggable)) {
                new CdsDraggableList(this);
                this.hasDraggableRows = true;
            }
        });
    }
    listenForColumnHiddenChange() {
        this.addEventListener('hiddenChange', (e) => {
            if (e.target.tagName.toLowerCase() === 'cds-grid-column') {
                this.cells.filter(c => c.col === e.target.col).forEach(cell => e.target.hidden ? cell.setAttribute('hidden', '') : cell.removeAttribute('hidden'));
                this.calculateColumns();
            }
        });
    }
    listenForCellKeyNaviation() {
        // on KEYDOWN move cell focus
        this.shadowRoot.querySelector('tbody').addEventListener('keydown', (e) => {
            if (e.code === 'ArrowUp' || e.code === 'ArrowDown' || e.code === 'ArrowLeft' || e.code === 'ArrowRight' || e.code === 'End' || e.code === 'Home' || e.code === 'PageUp' || e.code === 'PageDown') {
                const { x, y } = getNextCellCoordinate(e, this);
                this.cells.forEach(c => c.active = false);
                this.rows[y].cells[x].active = true;
                e.preventDefault();
            }
            if (e.target.tagName === 'TBODY' && e.code === 'Tab') {
                this.querySelector(`[active]`).scrollIntoView();
            }
        });
        this.addEventListener('click', (event) => {
            if (event.target.tagName === 'CDS-GRID-CELL') {
                this.cells.forEach(c => c.active = false);
                event.target.active = true;
            }
            if (event.target.tagName === 'CDS-PAGINATION-BUTTON') {
                this.setFirstCell();
            }
        });
    }
    listenColumnSort() {
        this.columns.forEach(col => {
            col.addEventListener('sortChange', (event) => {
                var _a;
                (_a = this.shadowRoot.querySelector(`#${event.target._id}`)) === null || _a === void 0 ? void 0 : _a.setAttribute('aria-sort', event.detail); // todo move to column 
                this.setFirstCell();
            });
        });
    }
    setFirstCell() {
        this.cells.forEach(c => c.active = false);
        const firstCell = this.cells[0];
        if (firstCell) {
            firstCell.firstCell = true;
            firstCell.active = true;
        }
    }
    initializeColumns() {
        this.columns
            .filter(column => !column.width && !column.hidden)
            .map(column => [column, parseInt(getComputedStyle(column.shadowRoot.querySelector('th')).width)])
            .forEach(([column, width]) => {
            this.style.setProperty(`--col-${column.col}-width`, column.width ? `${column.width}px` : `${width}px`);
        });
    }
    calculateColumns() {
        const cols = this.columns.filter(c => !c.hidden);
        this.style.setProperty('--grid-template-columns', cols.reduce((prev, column) => `${prev} ${`var(--col-${column.col}-width, 1fr)`}`, ''));
        cols
            .filter(column => column.width)
            .map(column => [column, `${column.width}px`])
            .forEach(([column, width]) => this.style.setProperty(`--col-${column.col}-width`, width));
        cols
            .filter(c => c.position === 'initial')
            .forEach(column => {
            this.style.setProperty(`--col-${column.col}-left`, 'initial');
            this.style.setProperty(`--col-${column.col}-position`, 'initial');
            this.style.setProperty(`--col-${column.col}-border-width`, '0');
        });
        cols
            .filter(c => c.position !== 'initial')
            .forEach(column => {
            this.style.setProperty(`--col-${column.col}-position`, 'sticky');
            this.style.setProperty(`--col-${column.col}-border-width`, '1px');
            this.style.setProperty(`--col-${column.col}-left`, column.position === 'sticky' ? '0' : `${column.th.offsetLeft}px`);
        });
        this.table.removeAttribute('rendering');
    }
};
__decorate([
    internalProperty()
], CdsGrid.prototype, "rowCount", void 0);
CdsGrid = __decorate([
    customElement('cds-grid')
], CdsGrid);
export { CdsGrid };
//# sourceMappingURL=grid.element.js.map